/**
 * Seasonal accent helper.
 *
 * Returns a subtle season string (spring | summer | autumn | winter)
 * based on the user's local month. Meant to drive a barely-noticeable
 * brand flourish on the home hero (a tinted bloom, a soft glow tone)
 * without ever touching the rose brand palette or the navigation
 * surfaces. Northern / southern hemispheres both work because the
 * "feeling" of a season in a brand context follows the calendar the
 * user experiences, not an astronomical definition.
 *
 * Pure function; safe to call inside a React render. Falls back to
 * 'spring' if something weird comes through, so the surface never
 * shows a missing class.
 */
export function currentSeason(now = new Date(), hemisphere = 'north') {
  let m;
  if (typeof now === 'number') {
    m = now;
  } else if (now && typeof now.getMonth === 'function') {
    m = now.getMonth();
  } else {
    // Unparseable input: fall back to a spring-range month so the doc
    // comment "falls back to spring" holds and the UI never briefly
    // flashes a winter wash on bad data.
    m = 3;
  }
  if (!Number.isInteger(m) || m < 0 || m > 11) m = 3;
  // Northern hemisphere mapping. Southern flips 6 months so a Brazilian
  // user sees "autumn" in April, which matches the feeling outside her
  // window. Default hemisphere is 'north' since en-US / en-GB are the
  // dominant locales today; Portuguese defaults to 'south' below.
  if (hemisphere === 'south') m = (m + 6) % 12;
  if (m >= 2 && m <= 4)  return 'spring';
  if (m >= 5 && m <= 7)  return 'summer';
  if (m >= 8 && m <= 10) return 'autumn';
  return 'winter';
}

/**
 * Hemisphere inferred from the active app language. Portuguese (pt)
 * defaults to southern (Brazil is our primary pt locale); everything
 * else defaults to northern. Overridable via a future user preference
 * if a northern-Portuguese or southern-English user wants to flip it.
 */
export function hemisphereFor(lang) {
  return lang === 'pt' ? 'south' : 'north';
}
